import { useCallback, useEffect, useRef, useState } from 'react';
import { app, describeError, enterChildMode, go, resetWizard } from '../state/app';
import {
  approveVideo,
  getFamilyMe,
  getInFlightCommissions,
  getVideos,
  unapproveVideo,
} from '../api/endpoints';
import { setBalance } from '../lib/devlog';
import { poll } from '../lib/poll';
import type { InFlightCommission, Video } from '../api/types';

/**
 * The library + the approval gate. Approval is a state transition, modelled as
 * a REST subresource: POST publishes, DELETE pulls it back off the shelf.
 *
 * The child/parent split is a SERVER decision. This screen filters locally as
 * belt-and-braces on top of what the server already returned; it never asks
 * the server for a trust level.
 *
 * IT SHOWS FILMS THAT DO NOT EXIST YET, and it has to. The making screen tells
 * the parent "you can leave this page — it will be waiting in your library",
 * and until this screen asked GET /commissions as well as GET /videos that was
 * false for the whole minute or two a film takes: a commission has no video row
 * until the pipeline writes one, so the shelf was empty seconds after promising
 * otherwise. Jamie ordered a film and found nothing.
 */
export function Library() {
  const { videos, banner } = app.use();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [childView, setChildView] = useState(false);
  const [handingOver, setHandingOver] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [inFlight, setInFlight] = useState<InFlightCommission[]>([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getVideos();
      app.set({ videos: result.videos ?? [] });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    getFamilyMe()
      .then((me) => setBalance(me.creditBalance, me.ledger ?? []))
      .catch(() => undefined);
  }, [refresh]);

  /**
   * POLL ONLY WHILE SOMETHING IS ACTUALLY IN FLIGHT.
   *
   * poll() runs its first tick immediately and stops as soon as `done` holds,
   * so a shelf with nothing being made costs exactly one request and then goes
   * quiet — a parent who leaves this tab open on a finished library must not
   * have us waking their iPad every four seconds for the rest of the day.
   *
   * A film LEAVING the list is the interesting event: it means the pipeline
   * finished (or gave up), so the video row it produced is what to fetch next.
   * That is why the count is tracked in a ref — the parent should watch a film
   * turn into a real row without reloading, which is the whole promise the
   * making screen makes. A bare count is enough to notice that because nothing
   * on this screen can START a commission, so while it is up the number can
   * only fall.
   */
  const seen = useRef(0);
  useEffect(() => {
    const handle = poll({
      fetch: getInFlightCommissions,
      onValue: (rows) => {
        // NULL MEANS "THE REQUEST FAILED", NOT "NOTHING IS BEING MADE". Keep
        // the rows already on screen and let the next tick ask again; a cold
        // start on Flex Consumption is routine and must not blank a parent's
        // shelf, nor make a film look finished when it is still rendering.
        if (rows === null) return;
        setInFlight(rows);
        if (rows.length < seen.current) void refresh();
        seen.current = rows.length;
      },
      // Same reason: only an ANSWER of "none" ends the poll. An error keeps it
      // alive, bounded by timeoutMs below.
      done: (rows) => rows !== null && rows.length === 0,
      // Four seconds, not the wizard's two: nobody is staring at a progress
      // bar here, and this screen is the one a parent leaves open.
      intervalMs: 4000,
      timeoutMs: 15 * 60 * 1000,
    });
    return () => handle.cancel();
  }, [refresh]);

  const setApproval = async (video: Video, approved: boolean) => {
    setBusyId(video.videoId);
    setError(null);
    try {
      if (approved) await approveVideo(video.videoId);
      else await unapproveVideo(video.videoId);
      await refresh();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusyId(null);
    }
  };

  const shown = childView ? videos.filter((v) => v.status === 'published') : videos;

  /**
   * THE SAME FILM CAN BE IN BOTH LISTS FOR A MOMENT, and without this it draws
   * twice on a parent's shelf.
   *
   * pipeline/assemble.py finishes a film in TWO writes: it puts the video row
   * first, then updates the commission separately. Between them the video
   * exists AND the commission still says "generating", so /videos and
   * /commissions both truthfully return it. The window is normally
   * milliseconds — but the second write has no retry options on a free-tier
   * Cosmos where a 429 is plausible, so it can be much longer, and "my film is
   * listed twice" is a bug a parent notices immediately.
   *
   * commissionId is on both shapes, which is what makes this a join rather
   * than a guess. The VIDEO row wins: it is the finished thing, and it is the
   * one carrying the controls that let a parent approve and watch.
   */
  const shelved = new Set(videos.map((v) => v.commissionId).filter(Boolean));
  const stillMaking = inFlight.filter((made) => !shelved.has(made.commissionId));

  return (
    <>
      {/* The picture pane is deliberately EMPTY here. It used to render
          "🎬 your review queue" over a video count — Jamie: "we don't need
          that". It was restating the heading two inches to its right and
          counting things the list itself already shows. */}
      <div className="pane-img" />
      <div className="pane-q">
        <h1>{childView ? 'The shelf' : 'Library'}</h1>
        <p className="sub">
          Only what you have watched and approved reaches your child. Unapproving pulls
          it straight back off the shelf.
        </p>

        {banner && <div className="gate">{banner}</div>}
        {error && <div className="err">{error}</div>}

        {/* A class rather than an inline flex row, because the inline one could
            not wrap: on a 375px phone the fourth control ("new character") ran
            off the right edge of the screen with nothing to scroll it. */}
        <div className="libtools">
          <button className="ghost" onClick={() => setChildView((v) => !v)}>
            {childView ? 'parent view' : 'child view'}
          </button>
          <button className="ghost" onClick={() => void refresh()}>
            refresh
          </button>
          {/* The hand-over. Distinct from "child view", which is a preview of
              what the child would see while the parent keeps the controls; this one
              actually hands the device over and locks the way back. */}
          <button className="ghost" onClick={() => setHandingOver(true)}>
            hand to child
          </button>
          <button
            className="ghost"
            onClick={() => {
              resetWizard();
              go('gallery');
            }}
          >
            new character
          </button>
        </div>

        {handingOver && <SetPin onCancel={() => setHandingOver(false)} />}

        {loading && <div className="sub pulse">loading…</div>}

        {/* `inFlight` is in the condition, not just `videos`. A parent who has
            this second ordered their first film would otherwise be told
            "nothing here yet — commission an episode", one screen after doing
            exactly that. */}
        {!loading && videos.length === 0 && (childView || stillMaking.length === 0) && (
          <div className="empty">
            Nothing here yet. Commission an episode and it will land here for
            you to watch and approve before your child ever sees it.
          </div>
        )}

        {/* CHILD VIEW is a wall of pictures, not a list.
            It was rows of text with a raw status string ("published"), a
            segment count and an ISO timestamp — for an audience of 2-5 who
            cannot read, and to whom the picture IS the navigation. */}
        {childView ? (
          <div className="shelf">
            {shown.map((video) => (
              <button
                className="shelf-tile"
                key={video.videoId}
                onClick={() => {
                  app.set({ watchingId: video.videoId });
                  go('watch');
                }}
              >
                {video.posterUrl ? (
                  <img src={video.posterUrl} alt="" />
                ) : (
                  <span className="shelf-blank">🎬</span>
                )}
                <span className="shelf-play">▶</span>
              </button>
            ))}
          </div>
        ) : (
          <table className="films">
          {/* A TABLE, because the question this screen answers is "which of my
              films is my child allowed to see" — and that is a column, not
              something to be read off the wording of a button on each card.
              Jamie: "can we have more of a table view showing what's on the
              shelf and not? maybe a checkbox or some other UX device".

              THE CHECKBOX IS THE CONTROL, not a display of one. It replaces
              the "Add to the shelf" / "Take off the shelf" pair, which said the
              same thing twice — once as state and once as an action — and made
              a parent read the button to work out where the film currently
              stood. One tap now, in the column that already tells them. */}
            <thead>
              <tr>
                <th scope="col" className="col-art"><span className="vh">Picture</span></th>
                <th scope="col">Film</th>
                <th scope="col" className="col-shelf">On the shelf</th>
                <th scope="col" className="col-act"><span className="vh">Watch</span></th>
              </tr>
            </thead>
            <tbody>
              {/* FIRST, above the finished films: this is the one the parent
                  came to look for. */}
              {stillMaking.map((made) => (
                <MakingRow key={made.commissionId} film={made} />
              ))}
              {shown.map((video) => (
                <tr key={video.videoId}>
                  <td className="col-art">
                    {video.posterUrl
                      ? <img className="row-poster" src={video.posterUrl} alt="" />
                      : <span className="shelf-blank">🎬</span>}
                  </td>
                  <td>
                    <div className="title">{video.title || 'A little film'}</div>
                    {/* Plain English, not "1 segments · 2026-08-17T17:39:01". */}
                    <div className="meta">{friendlyWhen(video.createdAt)}</div>
                  </td>
                  <td className="col-shelf">
                    <label className="shelfbox">
                      <input
                        type="checkbox"
                        checked={video.status === 'published'}
                        disabled={busyId === video.videoId}
                        onChange={(e) => void setApproval(video, e.target.checked)}
                      />
                      {/* The accessible name carries the film, because "on the
                          shelf" repeated down a column tells a screen-reader
                          user which row they are in exactly never. */}
                      <span className="vh">
                        {video.title || 'A little film'} is on the shelf
                      </span>
                    </label>
                  </td>
                  <td className="col-act">
                    <button
                      className="ghost"
                      onClick={() => {
                        // The ID, not the row. /watch/<videoId> is a real URL
                        // and go() builds it from the store — and the watch
                        // screen never read anything else off a Video, because
                        // the only call it makes is playback-urls.
                        app.set({ watchingId: video.videoId });
                        go('watch');
                      }}
                    >
                      Watch
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}


/**
 * A film that has been ordered and is not here yet.
 *
 * NO BUTTONS, and that is the design rather than an omission. Every other row
 * on this screen ends in "Watch" or "Add to the shelf"; there is nothing to
 * watch and nothing to approve until the pipeline has finished, and a disabled
 * copy of those controls would only invite the tap it then refuses. The poster
 * (preview.py renders the opening scene in a few seconds, roughly ten times
 * sooner than the film it previews) and a plain sentence are the whole row.
 */
function MakingRow({ film }: { film: InFlightCommission }) {
  // 'failed' should not reach here — a film that failed is no longer in flight
  // — but the status is on the wire and rendering "being made" over a film that
  // has stopped would be the same false promise this row exists to fix.
  const failed = film.status === 'failed';
  return (
    // A <tr>, because this sits in the same table as the finished films. It
    // deliberately fills the SAME columns: a film being made is one of the
    // parent's films, just not a watchable one yet, and giving it its own
    // shape would put it outside the list it belongs in.
    <tr className="row-making">
      <td className="col-art">
        {film.posterUrl ? (
          <img className="row-poster" src={film.posterUrl} alt="" />
        ) : (
          // A poster-shaped space rather than no space: without it the rows
          // above and below sit at different indents and the list looks broken.
          <div className="row-poster row-poster-blank" aria-hidden="true">
            {failed ? '·' : '✨'}
          </div>
        )}
      </td>
      <td>
        <div className="title">{film.title || 'A little film'}</div>
        <div className="meta">
          {failed
            ? 'We couldn’t finish this one — your credits went back'
            : 'Being made — it will appear here on its own'}
          {film.createdAt ? ` · ordered ${friendlyWhen(film.createdAt)}` : ''}
        </div>
      </td>
      {/* NO CHECKBOX. There is nothing to put on a shelf yet, and an empty
          disabled box in the column would read as "this film is not approved"
          rather than "this film does not exist yet". */}
      <td className="col-shelf">
        {!failed && <span className="making-dots" aria-hidden="true">•••</span>}
      </td>
      <td className="col-act" />
    </tr>
  );
}

/**
 * Set the PIN on the way IN, because there is nowhere else to set it: the
 * parent is handing the device over right now, and a mode you cannot leave is
 * worse than one with no lock at all.
 */
function SetPin({ onCancel }: { onCancel: () => void }) {
  const [pin, setPin] = useState('');

  // enterChildMode, not app.set: the lock has to be written down as well as
  // set, or the first reload hands the parent app back to the child.
  const enter = () => enterChildMode(pin);

  return (
    <div className="gate" style={{ marginBottom: 10 }}>
      <p style={{ margin: '0 0 8px' }}>
        Pick a 4-digit code. You will need it to get back out.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="pin-input"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          placeholder="••••"
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
        />
        <button className="primary" disabled={pin.length !== 4} onClick={enter}>
          hand it over
        </button>
        <button className="ghost" onClick={onCancel}>
          cancel
        </button>
      </div>
    </div>
  );
}

/** "today at 5:39pm" beats "2026-08-17T17:39:01" on a parent's screen. */
function friendlyWhen(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `today at ${time}` : date.toLocaleDateString(undefined,
    { day: 'numeric', month: 'short' });
}
